import React, { useState, useEffect } from "react";
import { FaCalendarDay } from "react-icons/fa";
import { tableConstants } from "./EventTableConstant";
import Table from "../../../components/Table/Table";
import Loader from "../../../components/Loader/Loader";
import Modal from "../../../components/Modal/Modal";
import { enqueueSnackbar } from "notistack";
import EventForm from "./EventForm";
import CardHeader from "../../../components/Card/CardHeader";
import Pagination from "../../../components/Pagination/Pagination";
import {
  getEventCalendars,
  createEventCalendar,
  updateEventCalendar,
  deleteEventCalendar,
} from "../../../services/AppManagement/EventCalendarService";
import Button from "../../../components/Button/Button";

const EventList = () => {
  const [data, setData] = useState([]);
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const [isLoader, setIsLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitile] = useState("");
  const [description, setDescription] = useState("");
  const [is_holiday, setIsHoliday] = useState("0");
  const [id, setId] = useState(null);
  const [event_date, setEventDate] = useState({ start_date: "", end_date: "" });
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const parseEventDateRange = (value) => {
    if (!value) {
      return { startDate: "", endDate: "" };
    }

    const rawValue = String(value).trim();
    if (rawValue.includes(" to ")) {
      const [startDate, endDate] = rawValue.split(" to ");
      return { startDate: startDate || "", endDate: endDate || "" };
    }

    if (rawValue.includes(" - ")) {
      const [startDate, endDate] = rawValue.split(" - ");
      return { startDate: startDate || "", endDate: endDate || "" };
    }

    return { startDate: rawValue, endDate: rawValue };
  };

  const getEventDatePayload = () => {
    const startDate = event_date?.start_date || "";
    const endDate = event_date?.end_date || "";

    if (!startDate && !endDate) {
      return "";
    }

    if (startDate && endDate) {
      if (startDate === endDate) {
        return startDate;
      }
      return `${startDate} to ${endDate}`;
    }

    return startDate || endDate;
  };

  const changeHandler = (e) => {
    const { name, value, checked } = e.target;
    if (name === "title") setTitile(value);
    else if (name === "event_start_date")
      setEventDate((prev) => ({ ...prev, start_date: value }));
    else if (name === "event_end_date")
      setEventDate((prev) => ({ ...prev, end_date: value }));
    else {
      if (checked) setIsHoliday("1");
      else setIsHoliday("0");
    }
  };

  const handleDateRangeChange = (range) => {
    const formatDate = (date) => {
      if (!date) {
        return "";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setEventDate({
      start_date: formatDate(range?.from),
      end_date: formatDate(range?.to),
    });
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setDescription(data);
  };

  async function fetchEvent({ selected }) {
    try {
      setIsLoader(true);
      let page = selected + 1;
      const session_id = localStorage.getItem("session_id");
      const response = await getEventCalendars(session_id, page);
      if (response.status === 200) {
        setCurrentPage(page - 1);
        setLastPage(response.data.last_page);
        setPageSize(response.data.per_page);
        setData(response.data.data);
      }
    } catch (error) {
      console.error("API Error:", error.response);
    } finally {
      setIsLoader(false);
    }
  }

  useEffect(() => {
    fetchEvent({ selected: 0 });
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();
    const session_id = localStorage.getItem("session_id");
    const data = {
      title: title,
      description: description,
      is_holiday: is_holiday,
      session_id: session_id,
      event_date: getEventDatePayload(),
    };

    try {
      setIsLoader(true);
      let response;
      if (id) {
        data.id = id;
        data.status = 1;
        response = await updateEventCalendar(id, data);
      } else {
        response = await createEventCalendar(data);
      }
      if (response.status === 200 || response.status === 201) {
        setTitile("");
        setDescription("");
        setIsHoliday(false);
        setId(null);
        setEventDate({ start_date: "", end_date: "" });
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        closeModal();
        fetchEvent({ selected: 0 });
      } else {
        enqueueSnackbar(response.data.message.error, { variant: "error" });
      }
    } catch (error) {
      setIsLoader(false);
      console.error("API Error:", error);
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  };

  const showModel = (rowData) => {
    openModal(true);
    setId(rowData.id);
    setTitile(rowData.title);
    setDescription(rowData.description);
    setIsHoliday(rowData.is_holiday);
    const { startDate, endDate } = parseEventDateRange(rowData.event_date);
    setEventDate({ start_date: startDate, end_date: endDate });
  };
  const EventHandler = () => {
    openModal(true);
    setTitile("");
    setDescription("");
    setId(null);
    setEventDate({ start_date: "", end_date: "" });
    setIsHoliday("0");
  };

  const deleteHandler = async (rowData) => {
    try {
      setIsLoader(true);
      const response = await deleteEventCalendar(rowData.id);
      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        fetchEvent({ selected: 0 });
      }
    } catch (error) {
      console.error("API Error:", error.response);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="card">
      <CardHeader
        title="Event"
        icon={<FaCalendarDay />}
        button={
          permissions.includes("event.calendar.create") && (
            <Button type="submit" variant="primary" onClick={EventHandler}>
              Add Event
            </Button>
          )
        }
      />
      <div className="card-body">
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          size="lg"
          title={id ? "Update Event" : "Add Event"}
        >
          <EventForm
            changeHandler={changeHandler}
            onDateRangeChange={handleDateRangeChange}
            submitHandler={submitHandler}
            handleEditorChange={handleEditorChange}
            title={title}
            description={description}
            is_holiday={is_holiday}
            event_date={event_date}
          />
        </Modal>
        {isLoader ? (
          <Loader />
        ) : (
          <div className="table-responsive">
            {data && (
              <React.Fragment>
                <Table
                  cols={tableConstants(
                    permissions,
                    deleteHandler,
                    showModel,
                    currentPage,
                    pageSize,
                  )}
                  data={data}
                  hover={true}
                />
                <Pagination
                  totalPage={lastPage}
                  currentPage={currentPage}
                  handlePageChange={(i) => fetchEvent(i)}
                ></Pagination>
              </React.Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default EventList;
